import { useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Upload, FileText, ArrowLeft, Loader2, X, ShieldCheck, Trash2, PlusCircle, FilePlus, Search, ChevronLeft, ChevronRight } from 'lucide-react'

interface ApprovedStudent {
  id: number
  email: string
  studentName: string | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  approvedCount: number
  approvedStudents: ApprovedStudent[]
  totalCount: number
  currentPage: number
  perPage: number
  search: string
  urls: AppShellProps['urls'] & {
    dashboard: string
    index: string
    import: string
    clear: string
    addOne: string
    deleteOne: string
  }
  translations: AppShellProps['translations'] & {
    approvedStudents: string
    approvedStudentsHint: string
    approvedStudentsCount: string
    replaceList: string
    addToList: string
    clearList: string
    uploadApprovedList: string
    uploadCsvFile: string
    selectFile: string
    dragDrop: string
    replaceWarning: string
    appendHint: string
    noApprovedStudents: string
    recentEntries: string
    studentName: string
    email: string
    addManualEntry: string
    nameOptional: string
    addEntry: string
    backToDashboard: string
  }
}

const SUPPORTED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls']
const SUPPORTED_FILE_ACCEPT = [
  '.csv', '.xlsx', '.xls', 'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',')

function isSupportedImportFile(file: File) {
  const name = file.name.toLowerCase()
  return SUPPORTED_FILE_EXTENSIONS.some((ext) => name.endsWith(ext))
}

export default function Index(props: Props) {
  const { event, approvedCount, approvedStudents, totalCount, currentPage, perPage, search: initialSearch, urls, translations: t, ...shell } = props
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [submitting, setSubmitting] = useState<'replace' | 'append' | null>(null)
  const [clearing, setClearing] = useState(false)
  const [searchInput, setSearchInput] = useState(initialSearch)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  // Manual entry state
  const [manualEmail, setManualEmail] = useState('')
  const [manualName, setManualName] = useState('')
  const [addingManual, setAddingManual] = useState(false)

  const totalPages = Math.ceil(totalCount / perPage)

  function navigate(page: number, search = initialSearch) {
    router.get(urls.index, { page, search }, { preserveScroll: true })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    navigate(1, searchInput)
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault()
    setDragging(false)
    const droppedFile = e.dataTransfer.files?.[0]
    if (droppedFile && isSupportedImportFile(droppedFile)) setFile(droppedFile)
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0] ?? null
    setFile(selectedFile && isSupportedImportFile(selectedFile) ? selectedFile : null)
  }

  function handleUpload(mode: 'replace' | 'append') {
    if (!file) return
    setSubmitting(mode)
    const formData = new FormData()
    formData.append('import_file', file)
    formData.append('mode', mode)
    router.post(urls.import, formData as any, {
      forceFormData: true,
      onFinish: () => setSubmitting(null),
    })
  }

  function handleClear() {
    setClearing(true)
    router.delete(urls.clear, { onFinish: () => setClearing(false) })
  }

  function handleDeleteOne(student: ApprovedStudent) {
    if (!confirm(`Remove ${student.email} from the approved list?`)) return
    setDeletingId(student.id)
    const url = urls.deleteOne.replace(':id', String(student.id))
    router.delete(url, {
      data: { page: currentPage, search: initialSearch },
      onFinish: () => setDeletingId(null),
    })
  }

  function handleAddManual(e: React.FormEvent) {
    e.preventDefault()
    if (!manualEmail.trim()) return
    setAddingManual(true)
    router.post(urls.addOne, { email: manualEmail.trim(), student_name: manualName.trim() }, {
      onFinish: () => { setAddingManual(false); setManualEmail(''); setManualName('') },
    })
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.approvedStudents} — ${event.name}`} />

      <div className="mx-auto max-w-5xl space-y-6 py-8 px-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => router.visit(urls.dashboard)} className="text-[#91918E] hover:bg-[#F1F1EF]">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-[#37352F]">{t.approvedStudents}</h1>
              <p className="mt-1 text-sm text-[#91918E]">{event.name}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={() => router.visit(urls.dashboard)} className="text-[#91918E]">
            {t.backToDashboard}
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
          {/* ── Left column ── */}
          <div className="space-y-4">
            {/* Count card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#37352F]">
                  <ShieldCheck className="h-5 w-5 text-[#2383E2]" />
                  {t.approvedStudentsCount}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-semibold text-[#37352F]">{approvedCount.toLocaleString()}</p>
                <p className="mt-2 text-sm text-[#91918E]">{t.approvedStudentsHint}</p>
              </CardContent>
            </Card>

            {/* Upload card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[#37352F]">{t.uploadApprovedList}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current?.click()}
                  className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-8 transition-colors ${dragging ? 'border-[#2383E2] bg-blue-50' : 'border-[#E8E8E5] hover:border-[#91918E]'}`}
                >
                  {file ? (
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-[#2383E2]" />
                      <div>
                        <p className="text-sm font-medium text-[#37352F]">{file.name}</p>
                        <p className="text-xs text-[#91918E]">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null) }} className="rounded p-1 hover:bg-[#F1F1EF]">
                        <X className="h-4 w-4 text-[#91918E]" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mb-3 h-10 w-10 text-[#91918E]" />
                      <p className="text-sm font-medium text-[#37352F]">{t.selectFile}</p>
                      <p className="mt-1 text-xs text-[#91918E]">{t.dragDrop}</p>
                    </>
                  )}
                  <input ref={fileRef} type="file" accept={SUPPORTED_FILE_ACCEPT} className="hidden" onChange={handleChange} />
                </div>

                <div className="space-y-1">
                  <Button className="w-full bg-[#2383E2] hover:bg-[#1b6ec2]" disabled={!file || submitting !== null} onClick={() => handleUpload('append')}>
                    {submitting === 'append' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FilePlus className="mr-2 h-4 w-4" />}
                    {t.addToList}
                  </Button>
                  <p className="text-xs text-[#91918E]">{t.appendHint}</p>
                </div>

                <div className="space-y-1">
                  <Button variant="outline" className="w-full border-[#E8E8E5] text-[#37352F] hover:bg-[#F1F1EF]" disabled={!file || submitting !== null} onClick={() => handleUpload('replace')}>
                    {submitting === 'replace' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    {t.replaceList}
                  </Button>
                  <p className="text-xs text-amber-600">{t.replaceWarning}</p>
                </div>

                <Button variant="outline" className="w-full border-[#F3D1D1] text-[#B42318] hover:bg-[#FEF3F2]" disabled={approvedCount === 0 || clearing} onClick={handleClear}>
                  {clearing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                  {t.clearList}
                </Button>
              </CardContent>
            </Card>

            {/* Manual entry card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#37352F]">
                  <PlusCircle className="h-5 w-5 text-[#2383E2]" />
                  {t.addManualEntry}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddManual} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-[#37352F]">{t.email} *</Label>
                    <Input type="email" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} placeholder="email@example.com" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-[#37352F]">{t.nameOptional}</Label>
                    <Input type="text" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder={t.studentName} />
                  </div>
                  <Button type="submit" className="w-full bg-[#2383E2] hover:bg-[#1b6ec2]" disabled={!manualEmail.trim() || addingManual}>
                    {addingManual ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-2 h-4 w-4" />}
                    {t.addEntry}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* ── Right column: full list ── */}
          <Card className="flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <CardTitle className="text-[#37352F]">
                  {t.recentEntries}
                  {totalCount > 0 && <span className="ml-2 text-sm font-normal text-[#91918E]">({totalCount.toLocaleString()})</span>}
                </CardTitle>
                <form onSubmit={handleSearch} className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#91918E]" />
                    <Input
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search…"
                      className="pl-8 h-8 w-52 text-sm"
                    />
                  </div>
                  <Button type="submit" size="sm" variant="outline" className="h-8 px-3 text-xs">
                    Search
                  </Button>
                  {initialSearch && (
                    <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs text-[#91918E]" onClick={() => { setSearchInput(''); navigate(1, '') }}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </form>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              {approvedStudents.length === 0 ? (
                <p className="py-10 text-center text-sm text-[#91918E]">{t.noApprovedStudents}</p>
              ) : (
                <>
                  <div className="overflow-hidden rounded-lg border border-[#E8E8E5]">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#F1F1EF]">
                          <th className="px-4 py-3 text-left font-medium text-[#91918E]">{t.studentName}</th>
                          <th className="px-4 py-3 text-left font-medium text-[#91918E]">{t.email}</th>
                          <th className="px-4 py-3 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {approvedStudents.map((student) => (
                          <tr key={student.id} className="border-t border-[#E8E8E5] hover:bg-[#FAFAFA]">
                            <td className="px-4 py-3 text-[#37352F]">{student.studentName || '—'}</td>
                            <td className="px-4 py-3 text-[#37352F]">{student.email}</td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => handleDeleteOne(student)}
                                disabled={deletingId === student.id}
                                className="rounded p-1 text-[#91918E] hover:text-[#B42318] hover:bg-[#FEF3F2] transition-colors disabled:opacity-40"
                                title="Remove"
                              >
                                {deletingId === student.id
                                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  : <Trash2 className="h-3.5 w-3.5" />}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm text-[#91918E]">
                      <span>
                        {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, totalCount)} of {totalCount.toLocaleString()}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          disabled={currentPage <= 1}
                          onClick={() => navigate(currentPage - 1)}
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </Button>
                        <span className="px-2 tabular-nums">{currentPage} / {totalPages}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          disabled={currentPage >= totalPages}
                          onClick={() => navigate(currentPage + 1)}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  )
}


